import { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react'

// Map interaction modes
export type MapInteractionMode = 'none' | 'reach-id' | 'draw-roi'

// ROI Bounding Box interface
export interface ROIBounds {
  north: number
  south: number
  east: number
  west: number
}

interface MapInteractionContextType {
  // Current interaction mode
  mode: MapInteractionMode
  setMode: (mode: MapInteractionMode) => void
  
  // Reach ID selection state
  selectedReachId: string | null
  setSelectedReachId: (reachId: string | null) => void
  isSearchingReachId: boolean
  setIsSearchingReachId: (searching: boolean) => void
  canFetchForecast: boolean
  setCanFetchForecast: (canFetch: boolean) => void
  
  // ROI drawing state
  roiBounds: ROIBounds | null
  setRoiBounds: (bounds: ROIBounds | null) => void
  isDrawing: boolean
  setIsDrawing: (drawing: boolean) => void
  
  // Forecast state
  showForecastTable: boolean
  setShowForecastTable: (show: boolean) => void
  
  // Actions
  activateReachIdMode: () => void
  activateDrawRoiMode: () => void
  deactivateMode: () => void
  clearReachId: () => void
  clearRoi: () => void
}

const MapInteractionContext = createContext<MapInteractionContextType | undefined>(undefined)

export function MapInteractionProvider({ children }: { children: ReactNode }) {
  // Interaction mode
  const [mode, setModeInternal] = useState<MapInteractionMode>('none')
  
  // Reach ID state
  const [selectedReachId, setSelectedReachId] = useState<string | null>(null)
  const [isSearchingReachId, setIsSearchingReachId] = useState(false)
  const [canFetchForecast, setCanFetchForecast] = useState(false)
  
  // ROI state
  const [roiBounds, setRoiBounds] = useState<ROIBounds | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  
  // Forecast state
  const [showForecastTable, setShowForecastTable] = useState(false)

  // Set mode with cleanup
  const setMode = useCallback((newMode: MapInteractionMode) => {
    setModeInternal(newMode)
    // Reset drawing state when switching modes
    if (newMode !== 'draw-roi') {
      setIsDrawing(false)
    }
    if (newMode !== 'reach-id') {
      setIsSearchingReachId(false)
    }
  }, [])

  // Activate reach ID selection mode
  const activateReachIdMode = useCallback(() => {
    setMode('reach-id')
  }, [setMode])

  // Activate ROI drawing mode
  const activateDrawRoiMode = useCallback(() => {
    setMode('draw-roi')
    setIsDrawing(true)
  }, [setMode])

  // Deactivate current mode
  const deactivateMode = useCallback(() => {
    setMode('none')
  }, [setMode])

  // Clear reach ID data
  const clearReachId = useCallback(() => {
    setSelectedReachId(null)
    setCanFetchForecast(false)
    setIsSearchingReachId(false)
  }, [])

  // Clear ROI data
  const clearRoi = useCallback(() => {
    setRoiBounds(null)
    setIsDrawing(false)
  }, [])

  const value: MapInteractionContextType = useMemo(() => ({
    mode,
    setMode,
    selectedReachId,
    setSelectedReachId,
    isSearchingReachId,
    setIsSearchingReachId,
    canFetchForecast,
    setCanFetchForecast,
    roiBounds,
    setRoiBounds,
    isDrawing,
    setIsDrawing,
    showForecastTable,
    setShowForecastTable,
    activateReachIdMode,
    activateDrawRoiMode,
    deactivateMode,
    clearReachId,
    clearRoi,
  }), [
    mode,
    setMode,
    selectedReachId,
    isSearchingReachId,
    canFetchForecast,
    roiBounds,
    isDrawing,
    showForecastTable,
    activateReachIdMode,
    activateDrawRoiMode,
    deactivateMode,
    clearReachId,
    clearRoi,
  ])

  return (
    <MapInteractionContext.Provider value={value}>
      {children}
    </MapInteractionContext.Provider>
  )
}

export function useMapInteraction() {
  const context = useContext(MapInteractionContext)
  if (context === undefined) {
    throw new Error('useMapInteraction must be used within a MapInteractionProvider')
  }
  return context
}
